import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Settings as SettingsIcon, Database, Bell, Shield, Download, Key, Eye, EyeOff, Loader2, CheckCircle, RefreshCw, Save, Smartphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { auth } from "@/lib/api";
import { toast } from "sonner";

const API = '/api';

export default function Impostazioni() {
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({ nome_campo: "Campo Base ANC", timezone: "Europe/Rome", auto_backup_enabled: false });
  const [lastBackup, setLastBackup] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [tempGateMobile, setTempGateMobile] = useState('');
  const [tempGateMensa, setTempGateMensa] = useState('');
  const [editGateMobile, setEditGateMobile] = useState(false);
  const [editGateMensa, setEditGateMensa] = useState(false);
  const [tempAccessPin, setTempAccessPin] = useState('');
  const [editAccessPin, setEditAccessPin] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [editNome, setEditNome] = useState(false);
  const [editTz, setEditTz] = useState(false);
  const [tempNome, setTempNome] = useState("");
  const [tempTz, setTempTz] = useState("");

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch(API + '/admin/settings', { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setSettings(d); setTempNome(d.nome_campo); setTempTz(d.timezone); setTempGateMobile(d.gate_mobile_code || ''); setTempGateMensa(d.gate_mensa_code || ''); setTempAccessPin(d.access_pin || ''); }
    } catch(e) {}
  }, []);

  const loadLastBackup = useCallback(async () => {
    try {
      const res = await fetch(API + '/admin/backups', { credentials: 'include' });
      if (res.ok) { const d = await res.json(); if (d.items && d.items.length > 0) setLastBackup(d.items[0]); }
    } catch(e) {}
  }, []);

  useEffect(() => { loadSettings(); loadLastBackup(); }, [loadSettings, loadLastBackup]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { toast.error("Le password non coincidono"); return; }
    if (passwordForm.newPassword.length < 8) { toast.error("La nuova password deve essere di almeno 8 caratteri"); return; }
    setLoading(true);
    try {
      await auth.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success("Password aggiornata con successo");
      setPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) { toast.error(error.message || "Errore nel cambio password"); }
    finally { setLoading(false); }
  };

  const handleSaveSetting = async (key, value) => {
    setSettingsLoading(true);
    try {
      const res = await fetch(API + '/admin/settings', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: value }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const d = await res.json();
      if (d.ok) { setSettings(d.settings); toast.success("Impostazione salvata"); }
      else throw new Error(d.error || 'Errore');
    } catch(e) { toast.error("Errore: " + e.message); }
    finally { setSettingsLoading(false); setEditNome(false); setEditTz(false); }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await fetch(API + '/admin/backup', { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const d = await res.json();
      if (d.ok) { setLastBackup(d.backup); toast.success("Backup completato: " + d.backup.filename); }
      else throw new Error('Errore backup');
    } catch(e) { toast.error("Errore backup: " + e.message); }
    finally { setBackupLoading(false); }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await fetch(API + '/admin/export-all', { credentials: 'include' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition');
      const fn = cd ? cd.split('filename=')[1]?.replace(/"/g, '') : 'anc-export.zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = fn;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Export scaricato: " + fn);
    } catch(e) { toast.error("Errore export: " + e.message); }
    finally { setExportLoading(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="impostazioni-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Impostazioni</h1>
        <p className="text-slate-500 mt-1">Configurazione sistema e account</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account / Sicurezza */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Account e Sicurezza
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full" variant="outline">
                  <Key className="h-4 w-4 mr-2" /> Cambia Password
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cambia Password</DialogTitle>
                  <DialogDescription>Inserisci la password attuale e la nuova password</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Password Attuale</Label>
                    <div className="relative">
                      <Input type={showPasswords.current ? "text" : "password"} value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}>
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Nuova Password</Label>
                    <div className="relative">
                      <Input type={showPasswords.new ? "text" : "password"} value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}>
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Conferma Password</Label>
                    <div className="relative">
                      <Input type={showPasswords.confirm ? "text" : "password"} value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}>
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Aggiorna Password
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Notifiche */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" /> Notifiche
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div><Label>Notifiche Email</Label><p className="text-sm text-slate-500">Ricevi aggiornamenti via email</p></div>
              <Switch disabled />
            </div>
            <div className="flex items-center justify-between">
              <div><Label>Avvisi Turni</Label><p className="text-sm text-slate-500">Notifica turni scoperti</p></div>
              <Switch defaultChecked disabled />
            </div>
            <div className="flex items-center justify-between">
              <div><Label>Alert Emergenze</Label><p className="text-sm text-slate-500">Notifiche immediate per emergenze</p></div>
              <Switch defaultChecked disabled />
            </div>
            <p className="text-xs text-slate-400 text-center">Configurazione SMTP richiesta per attivare le notifiche</p>
          </CardContent>
        </Card>

        {/* Generale */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="h-5 w-5" /> Generale
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Nome Campo</Label>
                {editNome ? (
                  <div className="flex gap-2 mt-1">
                    <Input value={tempNome} onChange={(e) => setTempNome(e.target.value)} className="h-8" />
                    <Button size="sm" onClick={() => handleSaveSetting('nome_campo', tempNome)} disabled={settingsLoading}>
                      {settingsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditNome(false); setTempNome(settings.nome_campo); }}>X</Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">{settings.nome_campo}</p>
                )}
              </div>
              {!editNome && <Button variant="outline" size="sm" onClick={() => setEditNome(true)}>Modifica</Button>}
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Fuso Orario</Label>
                {editTz ? (
                  <div className="flex gap-2 mt-1">
                    <Input value={tempTz} onChange={(e) => setTempTz(e.target.value)} className="h-8" />
                    <Button size="sm" onClick={() => handleSaveSetting('timezone', tempTz)} disabled={settingsLoading}>
                      {settingsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditTz(false); setTempTz(settings.timezone); }}>X</Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">{settings.timezone}</p>
                )}
              </div>
              {!editTz && <Button variant="outline" size="sm" onClick={() => setEditTz(true)}>Modifica</Button>}
            </div>
          </CardContent>
        </Card>

        
      {/* Accesso Gate Esterno */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" /> Accesso Gate Esterno
          </CardTitle>
          <p className="text-sm text-muted-foreground">Codici di accesso per i gate da dispositivi esterni (6 caratteri alfanumerici minuscoli)</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="pb-3 mb-3 border-b border-slate-200">
            <Label className="text-sm font-medium">PIN Accesso Turno (6 cifre numeriche)</Label>
            <p className="text-xs text-muted-foreground mb-1">Codice comune a Gate e Mensa. Cambialo ad ogni turno.</p>
            <div className="flex gap-2 mt-1">
              {editAccessPin ? (
                <>
                  <Input value={tempAccessPin} onChange={e => setTempAccessPin(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="123456" maxLength={6} inputMode="numeric" pattern="[0-9]*" className="font-mono tracking-widest text-lg" />
                  <Button size="sm" onClick={() => { handleSaveSetting('access_pin', tempAccessPin); setEditAccessPin(false); }} disabled={settingsLoading || tempAccessPin.length !== 6}>Salva</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditAccessPin(false); setTempAccessPin(settings.access_pin || ''); }}>X</Button>
                </>
              ) : (
                <>
                  <code className="flex-1 bg-muted px-3 py-2 rounded text-lg font-mono tracking-widest">{settings.access_pin || <span className="text-muted-foreground italic text-sm">Non configurato</span>}</code>
                  <Button size="sm" variant="outline" onClick={() => setEditAccessPin(true)}>Modifica</Button>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Sarà richiesto agli operatori sui link <code>/gate-access/gate01</code> e <code>/gate-access/mensa1</code>.</p>
          </div>
          <div>
            <Label className="text-sm font-medium">Codice Gate Mobile</Label>
            <div className="flex gap-2 mt-1">
              {editGateMobile ? (
                <>
                  <Input value={tempGateMobile} onChange={e => setTempGateMobile(e.target.value.toLowerCase().replace(/[^a-z0-9]/g,'').slice(0,6))} placeholder="es: abc123" maxLength={6} className="font-mono tracking-widest" />
                  <Button size="sm" onClick={() => { handleSaveSetting('gate_mobile_code', tempGateMobile); setEditGateMobile(false); }} disabled={settingsLoading || (tempGateMobile.length > 0 && tempGateMobile.length !== 6)}>Salva</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditGateMobile(false); setTempGateMobile(settings.gate_mobile_code || ''); }}>X</Button>
                </>
              ) : (
                <>
                  <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono tracking-widest">{settings.gate_mobile_code || <span className="text-muted-foreground italic">Non configurato</span>}</code>
                  <Button size="sm" variant="outline" onClick={() => setEditGateMobile(true)}>Modifica</Button>
                </>
              )}
            </div>
            {settings.gate_mobile_code && <p className="text-xs text-muted-foreground mt-1">URL: {window.location.origin}/gate-access/{settings.gate_mobile_code}</p>}
          </div>
          <div>
            <Label className="text-sm font-medium">Codice Gate Mensa</Label>
            <div className="flex gap-2 mt-1">
              {editGateMensa ? (
                <>
                  <Input value={tempGateMensa} onChange={e => setTempGateMensa(e.target.value.toLowerCase().replace(/[^a-z0-9]/g,'').slice(0,6))} placeholder="es: xyz789" maxLength={6} className="font-mono tracking-widest" />
                  <Button size="sm" onClick={() => { handleSaveSetting('gate_mensa_code', tempGateMensa); setEditGateMensa(false); }} disabled={settingsLoading || (tempGateMensa.length > 0 && tempGateMensa.length !== 6)}>Salva</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditGateMensa(false); setTempGateMensa(settings.gate_mensa_code || ''); }}>X</Button>
                </>
              ) : (
                <>
                  <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono tracking-widest">{settings.gate_mensa_code || <span className="text-muted-foreground italic">Non configurato</span>}</code>
                  <Button size="sm" variant="outline" onClick={() => setEditGateMensa(true)}>Modifica</Button>
                </>
              )}
            </div>
            {settings.gate_mensa_code && <p className="text-xs text-muted-foreground mt-1">URL: {window.location.origin}/gate-access/{settings.gate_mensa_code}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Dati e Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" /> Dati e Backup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Ultimo Backup</Label>
                <p className="text-sm text-slate-500">
                  {lastBackup ? new Date(lastBackup.created_at).toLocaleString('it-IT') : 'Mai eseguito'}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleBackup} disabled={backupLoading} data-testid="btn-backup">
                {backupLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                Backup
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Backup Automatico</Label>
                <p className="text-sm text-slate-500">Backup giornaliero automatico</p>
              </div>
              <Switch checked={settings.auto_backup_enabled}
                onCheckedChange={(v) => handleSaveSetting('auto_backup_enabled', v)} />
            </div>
            <Button variant="outline" className="w-full" onClick={handleExport} disabled={exportLoading} data-testid="btn-export">
              {exportLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Esporta Tutti i Dati
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* About */}
      <Card>
        <CardHeader>
          <CardTitle>Informazioni Sistema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-slate-500">Versione</p><p className="font-medium">1.2.0</p></div>
            <div><p className="text-slate-500">Ambiente</p><p className="font-medium">Produzione</p></div>
            <div><p className="text-slate-500">Database</p><p className="font-medium">MongoDB</p></div>
            <div><p className="text-slate-500">Ultimo Aggiornamento</p><p className="font-medium">{new Date().toLocaleDateString('it-IT')}</p></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
